import { Box, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import type {
  BuyerBidV2EspdCategoryDto,
  BuyerBidV2EspdDto,
} from "../../../../autogen/bff-api";
import {
  EspdEmptyState,
  espdCategoryHasIssue,
  espdCategoryName,
  espdStatus,
  localizedText,
  safeId,
  StatusPill,
} from "../bid-detail/espdResponseRendering";
import { useDefaultedExpansion } from "../bid-detail/useDefaultedExpansion";
import { espdIndicatorAnswer } from "./espdIndicatorAnswer";
import { YesNoPill } from "./YesNoPill";

interface Props {
  espd: BuyerBidV2EspdDto | null | undefined;
  resetKey: string;
}

export const FocusExclusionGrounds = ({ espd, resetKey }: Props) => {
  const { t } = useTranslation();
  const categories = useMemo(() => espd?.categories ?? [], [espd?.categories]);
  const ids = useMemo(() => categories.map((category) => category.code), [categories]);
  const defaultExpanded = useMemo(
    () => categories.filter(espdCategoryHasIssue).map((category) => category.code),
    [categories],
  );
  const { expanded, toggle } = useDefaultedExpansion({ ids, defaultExpanded, resetKey });

  if (!espd?.hasResponse || categories.length === 0) return <EspdEmptyState />;

  return (
    <Stack spacing="3">
      <Text fontSize="sm" color="smMuted">
        {t("Exclusion grounds from the submitted ESPD response (Part III). Select a category to see the answer to each question.")}
      </Text>
      {categories.map((category) => (
        <CategoryCard
          key={category.code}
          category={category}
          isExpanded={expanded.includes(category.code)}
          onToggle={() => toggle(category.code)}
        />
      ))}
    </Stack>
  );
};

const CategoryCard = ({
  category,
  isExpanded,
  onToggle,
}: {
  category: BuyerBidV2EspdCategoryDto;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const status = espdStatus(category.deviationCount, category.unansweredCount, t);
  const panelId = `focus-espd-${safeId(category.code)}`;

  return (
    <Box borderWidth="1px" borderColor="smBorder" rounded="xl" bg="smBackground" overflow="hidden">
      <Flex
        as="button"
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        w="full"
        align="center"
        gap="3"
        px="4"
        py="3"
        textAlign="left"
        _hover={{ bg: "smBackgroundSecondary" }}
      >
        <Icon as={isExpanded ? FiChevronDown : FiChevronRight} color="smMuted" />
        <Text fontWeight="bold" color="smText">
          {espdCategoryName(category, t)}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("espd_question_count", { count: category.criteria.length })}
        </Text>
        <Box ml="auto">
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </Box>
      </Flex>

      {isExpanded && (
        <Stack id={panelId} spacing="0" px="4" pb="3">
          {category.criteria.map((criterion) => (
            <Flex
              key={criterion.id}
              align="center"
              gap="4"
              py="3"
              borderTopWidth="1px"
              borderColor="smBorderSubtle"
            >
              <Text fontSize="sm" color="smText" flex="1" minW="0">
                {localizedText(
                  i18n.language,
                  criterion.nameSource,
                  criterion.nameTranslation,
                  criterion.name,
                )}
              </Text>
              <YesNoPill value={espdIndicatorAnswer(criterion)} />
            </Flex>
          ))}
        </Stack>
      )}
    </Box>
  );
};
